import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileImage, File, Share2, Trash2 } from "lucide-react";
import { JournalEntry, JournalAttachment } from "@/types/journal";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AttachmentList from "./AttachmentList";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/utils/dateFormat";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface JournalEntryListProps {
  entries: JournalEntry[] | undefined;
  entryAttachments: Record<string, JournalAttachment[]>;
  onAttachmentDelete: (entryId: string, attachmentId: string) => void;
  showAll?: boolean;
  deletable?: boolean;
  onDeleteSelected?: (entryIds: string[]) => Promise<void>;
}

const JournalEntryList: React.FC<JournalEntryListProps> = ({ 
  entries, 
  entryAttachments,
  onAttachmentDelete,
  showAll = false,
  deletable = false,
  onDeleteSelected,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [hasTherapist, setHasTherapist] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const checkTherapistConnection = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: relationships, error } = await supabase
          .from('client_therapist_relationships')
          .select('*')
          .eq('client_id', user.id)
          .eq('status', 'active')
          .limit(1);

        if (error) throw error;
        const hasActiveTherapist = relationships && relationships.length > 0;
        setHasTherapist(hasActiveTherapist);

        // If client has a therapist, update all entries to be shared
        if (hasActiveTherapist) {
          const { error: updateError } = await supabase
            .from('journal_entries')
            .update({ shareable_with_therapist: true })
            .eq('user_id', user.id)
            .is('shareable_with_therapist', null);

          if (updateError) throw updateError;
          await queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
        } else {
          // If no therapist, ensure all entries are marked as not shared
          const { error: updateError } = await supabase
            .from('journal_entries')
            .update({ shareable_with_therapist: false })
            .eq('user_id', user.id);

          if (updateError) throw updateError;
          await queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
        }
      } catch (error) {
        console.error('Error checking therapist connection:', error);
      }
    };

    checkTherapistConnection();
  }, [queryClient]);

  const truncateContent = (content: string, maxLines: number = 5) => {
    const lines = content.split('\n');
    if (lines.length <= maxLines) return content;
    return lines.slice(0, maxLines).join('\n') + '\n...';
  };

  const handleShareToggle = async (entryId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('journal_entries')
        .update({ 
          shareable_with_therapist: !currentStatus 
        } as Partial<JournalEntry>)
        .eq('id', entryId);

      if (error) throw error;

      toast.success(`Entry ${!currentStatus ? 'shared' : 'unshared'} with therapist`);
      await queryClient.invalidateQueries({ queryKey: ["journal-entries"] });
    } catch (error) {
      console.error('Error updating share status:', error);
      toast.error('Failed to update sharing status');
    }
  };

  const getEntryBackgroundClass = (emotionType: string | null | undefined) => {
    const negativeEmotions = ['Guilty', 'Angry', 'Frustrated', 'Anxious', 'Sad', 'Overwhelmed'];
    if (emotionType && negativeEmotions.includes(emotionType)) {
      return "bg-gradient-to-r from-red-50 to-white border-red-200";
    }
    return "bg-gradient-to-r from-[#E8F0F8] to-white border-blue-200";
  };

  const entriesToShowForSelect = showAll ? entries : (entries || []).slice(0, 5);
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(entriesToShowForSelect.map((e) => e.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const handleConfirmDelete = async () => {
    if (selectedIds.size === 0 || !onDeleteSelected) return;
    setIsDeleting(true);
    try {
      await onDeleteSelected(Array.from(selectedIds));
      clearSelection();
      setDeleteDialogOpen(false);
    } catch (e) {
      toast.error("Failed to delete entries");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!entries || entries.length === 0) {
    return (
      <Card className="bg-white backdrop-blur-sm border border-[#CCCCCC]">
        <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
          <CardTitle className="text-[#333B44]">Journal Entries</CardTitle>
          <CardDescription>Your journal is empty</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Create your first entry using the form above.
          </p>
        </CardContent>
      </Card>
    );
  }

  const entriesToShow = showAll ? entries : entries.slice(0, 5);

  if (!showAll) {
    // Horizontal scroll for last five journal entries
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#333B44]">Last Five Journal Entries</h2>
          {deletable && onDeleteSelected && (
            <div className="flex items-center gap-2">
              {selectedIds.size > 0 ? (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={clearSelection}>Clear</Button>
                  <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
                    <Trash2 className="h-4 w-4 mr-1" /> Delete ({selectedIds.size})
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={selectAll}>Select to delete</Button>
              )}
            </div>
          )}
        </div>
        <div className="flex space-x-4 overflow-x-auto pb-2">
          {entriesToShow.map(entry => {
            const headerBackgroundClass = getEntryBackgroundClass(entry.emotion_type);
            const isNegativeEmotion = ['Sad', 'Angry', 'Frustrated', 'Anxious'].includes(entry.emotion_type || '');
            const entryAttachmentsList = entryAttachments[entry.id] || [];
            const isSelected = deletable && selectedIds.has(entry.id);
            return (
              <Card 
                key={entry.id} 
                className={`min-w-[260px] max-w-[320px] bg-white backdrop-blur-sm hover:shadow-lg transition-all duration-300 animate-fadeIn flex-shrink-0 ${isNegativeEmotion ? 'border-red-200' : 'border-blue-200'} ${isSelected ? 'ring-2 ring-[#333B44]' : ''}`}
              >
                <CardHeader className={`${headerBackgroundClass} rounded-t-lg`}>
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-2">
                      {deletable && (
                        <Checkbox
                          checked={selectedIds.has(entry.id)}
                          onCheckedChange={() => toggleSelect(entry.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1"
                        />
                      )}
                      <div>
                        <CardTitle className={`text-lg ${isNegativeEmotion ? 'text-red-700' : 'text-blue-700'}`}>{entry.mood}</CardTitle>
                        <CardDescription>{formatDate(entry.created_at)}</CardDescription>
                      </div>
                    </div>
                    {entry.emotion_type && (
                      <div className="text-right">
                        <div className={`font-medium ${isNegativeEmotion ? 'text-red-600' : 'text-blue-600'}`}>{entry.emotion_type}</div>
                        <div className="text-sm text-muted-foreground">Intensity: {entry.emotion_intensity}/10</div>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start">
                    {entry.media_type === 'video' && entry.media_url && (
                      <div className="mr-3 flex-shrink-0">
                        <video
                          src={entry.media_url}
                          className="w-full max-w-[84px] max-h-[56px] rounded object-cover"
                          muted
                          playsInline
                          preload="metadata"
                          onMouseOver={e => (e.currentTarget as HTMLVideoElement).play()}
                          onMouseOut={e => (e.currentTarget as HTMLVideoElement).pause()}
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {truncateContent(entry.content)}
                      </p>
                      {entryAttachments[entry.id] && entryAttachments[entry.id].length > 0 && (
                        <div className="mt-4">
                          <AttachmentList
                            attachments={entryAttachments[entry.id]}
                            onDelete={(attachmentId) => onAttachmentDelete(entry.id, attachmentId)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor={`share-${entry.id}`} className="flex items-center space-x-2 text-sm">
                        <Share2 className={`h-4 w-4 ${hasTherapist ? (entry.shareable_with_therapist ? 'text-green-600' : 'text-red-600') : 'text-gray-400'}`} />
                        <span className={hasTherapist ? (entry.shareable_with_therapist ? 'text-green-600' : 'text-red-600') : 'text-gray-400'}>
                          {hasTherapist ? (entry.shareable_with_therapist ? 'Shared with therapist' : 'Private') : 'No therapist connected'}
                        </span>
                      </Label>
                      <Switch
                        id={`share-${entry.id}`}
                        checked={hasTherapist && entry.shareable_with_therapist}
                        onCheckedChange={(checked) => {
                          if (hasTherapist) {
                            handleShareToggle(entry.id, entry.shareable_with_therapist);
                          }
                        }}
                        disabled={!hasTherapist}
                        className={`${hasTherapist ? (entry.shareable_with_therapist ? 'bg-green-600' : 'bg-red-600') : 'bg-gray-300'}`}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <Button size="sm" className="w-full" onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/journal/entry/${entry.id}`);
                    }}>View</Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        {deletable && onDeleteSelected && (
          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete journal entries?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete {selectedIds.size} {selectedIds.size === 1 ? "entry" : "entries"} and any attachments. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    handleConfirmDelete();
                  }}
                  disabled={isDeleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    );
  }

  // Default vertical list for showAll
  return (
    <div>
    <div className="space-y-4">
      {deletable && onDeleteSelected && (
        <div className="flex items-center justify-between gap-2 flex-wrap bg-white/80 backdrop-blur-sm border border-[#CCCCCC] rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={selectAll}>Select all</Button>
            <Button variant="outline" size="sm" onClick={clearSelection}>Clear selection</Button>
          </div>
          {selectedIds.size > 0 ? (
            <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-1" /> Delete {selectedIds.size} {selectedIds.size === 1 ? 'entry' : 'entries'}
            </Button>
          ) : (
            <span className="text-sm text-muted-foreground">Select entries to delete</span>
          )}
        </div>
      )}
      {entriesToShow.map((entry) => {
        const isNegativeEmotion = ['Sad', 'Angry', 'Frustrated', 'Anxious', 'Guilty', 'Overwhelmed'].includes(entry.emotion_type || '');
        const isSelected = deletable && selectedIds.has(entry.id);
        return (
          <Card 
            key={entry.id} 
            id={entry.id}
            className={`${getEntryBackgroundClass(entry.emotion_type)} backdrop-blur-sm cursor-pointer hover:shadow-md transition-all duration-200 ${isSelected ? 'ring-2 ring-[#333B44]' : ''}`}
            onClick={() => navigate(`/journal/entry/${entry.id}`)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center gap-3">
                {deletable && (
                  <Checkbox
                    checked={selectedIds.has(entry.id)}
                    onCheckedChange={() => toggleSelect(entry.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <div>
                  <CardTitle className={`text-lg font-medium ${isNegativeEmotion ? 'text-red-700' : 'text-blue-700'}`}>
                    {entry.mood || "How are you feeling?"}
                  </CardTitle>
                  <CardDescription>
                    {formatDate(entry.created_at)}
                  </CardDescription>
                  {entry.emotion_type && (
                    <div className="mt-1">
                      <span className={`text-sm font-medium ${isNegativeEmotion ? 'text-red-600' : 'text-blue-600'}`}>{entry.emotion_type}</span>
                      <span className="text-sm text-muted-foreground ml-2">Intensity: {entry.emotion_intensity}/10</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Label htmlFor={`share-${entry.id}`} className="flex items-center space-x-2 text-sm">
                  <Share2 className={`h-4 w-4 ${hasTherapist ? (entry.shareable_with_therapist ? 'text-green-600' : 'text-red-600') : 'text-gray-400'}`} />
                  <span className={hasTherapist ? (entry.shareable_with_therapist ? 'text-green-600' : 'text-red-600') : 'text-gray-400'}>
                    {hasTherapist ? (entry.shareable_with_therapist ? 'Shared with therapist' : 'Private') : 'No therapist connected'}
                  </span>
                </Label>
                <Switch
                  id={`share-${entry.id}`}
                  checked={hasTherapist && entry.shareable_with_therapist}
                  onCheckedChange={(checked) => {
                    if (hasTherapist) {
                      handleShareToggle(entry.id, entry.shareable_with_therapist);
                    }
                  }}
                  disabled={!hasTherapist}
                  className={`${hasTherapist ? (entry.shareable_with_therapist ? 'bg-green-600' : 'bg-red-600') : 'bg-gray-300'}`}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-start">
                {entry.media_type === 'video' && entry.media_url && (
                  <div className="mr-3 flex-shrink-0">
                    <video
                      src={entry.media_url}
                      className="w-full max-w-[84px] max-h-[56px] rounded object-cover"
                      muted
                      playsInline
                      preload="metadata"
                      onMouseOver={e => (e.currentTarget as HTMLVideoElement).play()}
                      onMouseOut={e => (e.currentTarget as HTMLVideoElement).pause()}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {truncateContent(entry.content)}
                  </p>
                  {entryAttachments[entry.id] && entryAttachments[entry.id].length > 0 && (
                    <div className="mt-4">
                      <AttachmentList
                        attachments={entryAttachments[entry.id]}
                        onDelete={(attachmentId) => onAttachmentDelete(entry.id, attachmentId)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete journal entries?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete {selectedIds.size} {selectedIds.size === 1 ? 'entry' : 'entries'} and any attachments. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); handleConfirmDelete(); }}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </div>
  );
};

export default JournalEntryList;
